import React from "react";
import { Element } from "react-scroll";
import { useSelector } from "react-redux";

const image = (file) => `https://firebasestorage.googleapis.com/v0/b/vert-farm-api.appspot.com/o/web%2Fimg%2F${file}?alt=media`;

const text = {
  paragraph1: [
    "Vi på VERT.farm vill att färskt grönt ska ha en självklar plats på tallriken. Därför odlar vi färska och smakrika microgreens i vår moderna vertikala inomhusodling, där de får växa under goda förhållanden.",
    "At VERT.farm, we want to make it easy to enjoy more fresh greens in your meals. Our modern indoor vertical farm gives us the right conditions to grow fresh, flavourful microgreens.",
  ],
  paragraph2: [
    "Microgreens är späda grönsaksskott med mycket smak och näring. De passar i sallader, på smörgåsar och i smoothies. Det är ett enkelt sätt att få i sig vitaminer, mineraler och antioxidanter.",
    "Microgreens are young vegetable shoots full of flavour and nutrients. They're a tasty addition to salads, sandwiches and smoothies, and an easy way to add vitamins, minerals and antioxidants to your meals.",
  ],
  paragraph3: [
    "Vi använder ett egenutvecklat system som ger oss kontroll över odlingsmiljön. Odlingsbrickorna står i flera våningar, så att vi kan använda utrymmet effektivt. Vi anpassar belysningen och odlar hydroponiskt för att ge växterna det de behöver.",
    "We use a growing system developed in-house to create a controlled environment for our microgreens. Stacking the growing trays makes good use of the space, while our lighting and hydroponic systems give the plants what they need to thrive.",
  ],
  paragraph4: [
    "Hållbarhet är viktigt för oss. Därför använder vi giftfria material av hög kvalitet och återanvänder cirka 90 % av vattnet i odlingen. Det hjälper oss att hushålla med resurserna.",
    "Sustainability is an important part of how we grow. We use high-quality, non-toxic materials and reuse around 90% of our water to make better use of our resources.",
  ],
  paragraph5: [
    "Vi tycker att microgreens smakar bäst när de är färska. Därför skördar vi dem samma dag som vi levererar, så att du får dem nyskördade.",
    "When it comes to flavour, we believe fresh is best. That's why we harvest our microgreens on the day of delivery, so you can enjoy them freshly picked.",
  ],
  paragraph6: [
    "Prova våra färska, näringsrika microgreens från VERT.farm – smaken får tala för sig själv.",
    "Try fresh, nutritious microgreens from VERT.farm and taste the difference for yourself.",
  ],
  subtitle1: ["Färskt och näringsrikt", "Fresh and nutritious"],
  subtitle2: [
    "Vi odlar utan bekämpningsmedel och skördar samma dag som vi levererar.",
    "Greens grown without pesticides, harvested on the day of delivery.",
  ],
  sideTitle1: ["Färskt och näringsrikt", "Fresh and nutritious"],
  sideText1: [
    "Vi odlar våra microgreens inomhus under jämna förhållanden. De späda skotten har mycket smak och näring.",
    "We grow our microgreens indoors in a controlled environment, with a focus on flavour and nutrition.",
  ],
  sideTitle2: ["Färska skott året runt", "Available all year round"],
  sideText2: [
    "Vår inomhusodling gör att vi kan skörda färska microgreens året runt, oavsett hur vädret ser ut.",
    "Growing indoors means we can produce microgreens all year round, whatever the weather outside.",
  ],
  sideTitle3: ["Med omtanke om miljön", "Care for the environment"],
  sideText3: [
    "Genom att odla på höjden tar vi vara på utrymmet. Dessutom återanvänder vi en stor del av vattnet.",
    "Vertical indoor farming helps us use water and growing space efficiently and make the most of our resources.",
  ],
  sideTitle4: ["Utan bekämpningsmedel", "Grown without pesticides"],
  sideText4: [
    "Inomhus har vi kontroll över odlingen och behöver inga bekämpningsmedel.",
    "Our controlled indoor environment allows us to grow without pesticides.",
  ],
  sideTitle5: ["Stöd lokala odlare", "Support local growers"],
  sideText5: [
    "När du köper lokalt odlat stöttar du odlare i närheten och bidrar till kortare transporter.",
    "Choosing locally grown food supports growers in your area and helps keep transport distances down.",
  ],
};

const growPhotos = ["mizuna.jpg", "shelf.jpeg", "kale.jpg"];
const platePhotos = ["products%2Fpakchoi4.jpg", "products%2Famaranth4.jpg", "products%2Fasianmix4.jpeg", "products%2Fredcabbage4.jpg"];
const factPhotos = ["bullsblood.jpg", "cress2.jpg", "sango.jpg", "redkale.jpg", "fenugreek.jpg"];

export default function FarmInfo() {
  const language = useSelector((state) => state.app.language);
  return (
    <Element name="farm/info" className="FarmInfo" id="farm-info">
      <div className="FarmLandingContainer FarmInfoLayout">
        <div className="FarmInfoStory">
          <header className="FarmLandingSectionHeading">
            <h2>{text.subtitle1[language]}</h2>
            <p className="FarmLandingSectionIntro">{text.paragraph1[language]}</p>
          </header>
          <p className="FarmInfoStoryLead">{text.paragraph2[language]}</p>
          <div className="FarmInfoPhotoStrip" aria-label={text.subtitle1[language]}>
            {growPhotos.map((file, index) => <img key={file} src={image(file)} alt={language === 0 ? `Mikrogrönt i odlingen ${index + 1}` : `Microgreens growing ${index + 1}`} loading="lazy" />)}
          </div>
          <div className="FarmInfoStoryColumns"><p>{text.paragraph3[language]}</p><p>{text.paragraph4[language]}</p></div>
          <blockquote>{text.subtitle2[language]}</blockquote>
          <div className="FarmInfoPlateStrip" aria-label={text.subtitle2[language]}>
            {platePhotos.map((file, index) => <img key={file} src={image(file)} alt={language === 0 ? `Mikrogrönt på tallriken ${index + 1}` : `Microgreens on a plate ${index + 1}`} loading="lazy" />)}
          </div>
          <div className="FarmInfoStoryClosing"><p>{text.paragraph5[language]}</p><p>{text.paragraph6[language]}</p></div>
        </div>
        <aside className="FarmInfoFacts" aria-label={language === 0 ? "Om vår odling" : "About our farm"}>
          {factPhotos.map((file, index) => <article className="FarmInfoFact" key={file}>
            <img src={image(file)} alt="" loading="lazy" />
            <div className="FarmInfoFactCopy"><h3>{text[`sideTitle${index + 1}`][language]}</h3><p>{text[`sideText${index + 1}`][language]}</p></div>
          </article>)}
        </aside>
      </div>
    </Element>
  );
}
